/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-empty-function */
import React, { CSSProperties } from 'react';
import { createPortal, findDOMNode } from 'react-dom';
import styles from './index.css';
import classNames from 'classnames';
import ListView, { Orientation } from '@com/pc-left-thumbnail-list/list-view';
import RegionWrapper from '../region-wrapper';
import PopupWrapper, { PopupMenuType } from '@com-pc/popup-wrapper/index';
import SlideCellWrapper from '@com/pc-left-thumbnail-list/cell-wrapper';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import SectionTitle from './list-view/section-title';
import DragIndicator from './drag-indicator';
import Clearance from './clearance';
import { ISectionModel } from '@src/slide/domain/ui-model/element-modeles/Interface/section-model.interface';
import ClearanceUtils from '@src/slide/domain/ui-model/utils/clearance-utils';
import { EDIT_TYPE } from '@src/slide/domain/selection/constant/edit-type';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IGapModel } from '@src/slide/domain/ui-model/element-modeles/Interface/gap-model.interface';
import { IMasterModel } from '@src/slide/domain/ui-model/element-modeles/Interface/master-model.interface';
import {
    IPcLeftThumbnailListProps,
    IPcLeftThumbnailListService,
    IPcLeftThumbnailListState,
} from '@src/slide/application/service/pc-left-thumbnail-list/core/type';
import ReactView from '@src/slide/infrastructure/module/independent/view-service-define/react/react-view';
import PcLeftThumbnailListModel from '@src/slide/application/service/pc-left-thumbnail-list/core/pc-left-thumbnail-list-model';

export interface IRenderClearanceOptions {
    clearanceIndex: number;
    selectedClearanceIndex: number;
    style?: CSSProperties;
    isSectionFolded?: boolean;
}

// slide列表可视区域，上下加载/释放内存的缓冲宽度，目前先制定不留buffer
const VIEWABLE_BUFFER_LENGTH = 0;

export default class PcLeftThumbnailList extends React.Component<IPcLeftThumbnailListProps, IPcLeftThumbnailListState> {
    /**
     * 组件对应的View Service
     * @private
     */
    private slideListService: IPcLeftThumbnailListService;

    // refs
    private refMap = new Map<string, any>();

    public constructor(props, context) {
        super(props, context);
        this.slideListService = props.slideListService;
        this.slideListService.setViewDelegate(new class extends ReactView<IPcLeftThumbnailListProps> {
            public getElementById(id: string) {
                return (this.realView as PcLeftThumbnailList)?.getRef(id);
            }
        }(this));
    }

    // 生命周期方法------------start
    public componentDidMount() {
        this.slideListService.componentDidMount();
    }

    public shouldComponentUpdate(nextProps, nextState): boolean {
        return this.slideListService.shouldComponentUpdate(nextProps, nextState);
    }

    public componentDidUpdate(prevProps, prevState) {
        this.slideListService.componentDidUpdate(prevProps, prevState);
    }

    public componentWillUnmount() {
        this.slideListService.componentWillUnmount();
        this.slideListService.unBindViewDelegate();
    }
    // 生命周期方法------------end

    public getRef(name: string) {
        return this.refMap.get(name);
    }

    public render(): React.ReactNode {
        return (
            <div className={styles.list} style={this.props.style}>
                {this.renderSlidesListView()}
                {this.renderMasterLayoutListView()}
            </div>
        );
    }

    private getCommonRenderProps(viewType: string) {
        const { slideHeight, editType, sectionWidth } = this.getState();
        const {
            getCellID,
            handleSlideListScroll,
            handleCellAppear,
            handleCellDisappear,
            handleVisibleCellIDsChange,
            getSlideCellClass,
        } = this.slideListService;
        const listWrapperClass = classNames({ [styles.listWrapper]: true });
        return {
            className: listWrapperClass,
            orientation: Orientation.vertical,
            getCellHeight: () => slideHeight,
            onScroll: handleSlideListScroll,
            getCellID,
            onCellAppearAtIndex: handleCellAppear,
            onCellDisappearAtIndex: handleCellDisappear,
            onVisibleCellIDsChange: handleVisibleCellIDsChange,
            editType,
            cellClass: getSlideCellClass(viewType),
            groupWrapperStyle: this.slideListService.getCurGroupWrapperStyle(),
            slideHeight,
            sectionWidth,
        };
    }

    // 母版列表和普通列表功能区别不大 尽量和 renderSlidesListView 合并
    private renderMasterLayoutListView = () => {
        const {
            editType,
            isFirstSlideShow,
        } = this.getState();
        const isShow = editType === EDIT_TYPE.MASTER_LAYOUT;
        const originList = isFirstSlideShow ? this.slideListService.getMasterLayoutGroupList() : [[]];
        const list = originList;
        const displayStateIds = this.flattenAndFilterSlideModelIds(list);
        return (
            <React.Fragment>
                <RegionWrapper region={RegionType.SLIDELIST} style={isShow ? {} : { display: 'none' }}>
                    <PopupWrapper type={PopupMenuType.LAYOUT_MASTER_GAP} style={{ display: 'flex' }}>
                        <ListView
                            {...this.getCommonRenderProps(EDIT_TYPE.MASTER_LAYOUT)}
                            dataSource={list}
                            ref={refDom => this.setListRef(refDom, this.getListWrapperName(EDIT_TYPE.MASTER_LAYOUT))}
                            getCellView={this.getCellView}
                            getCellHeight={this.getCellHeight}
                            displayStateIds={displayStateIds}
                            viewType={EDIT_TYPE.MASTER_LAYOUT}
                        />
                    </PopupWrapper>
                </RegionWrapper>
            </React.Fragment>
        );
    };

    private renderSlidesListView = () => {
        const {
            editType,
            isPressed,
            isFirstSlideShow,
            isShowingHistory,
        } = this.getState();
        const isShow = editType === EDIT_TYPE.SLIDE;
        const originList = isFirstSlideShow ? this.slideListService.getRenderModelList() : [[]];
        // 不展示的时候没有必要去筛选出dragging的slide，不会update
        const list = originList;
        const displayStateIds = this.flattenAndFilterSlideModelIds(list);
        return (<React.Fragment>
            {(isShowingHistory && isShow) ? this.renderHistoryAuthorTips() : null}
            <RegionWrapper region={RegionType.SLIDELIST} style={isShow ? {} : { display: 'none' }}>
                <PopupWrapper type={PopupMenuType.SLIDE_GAP} style={{ display: 'flex' }}>
                    <ListView
                        loadSlideBuffer={VIEWABLE_BUFFER_LENGTH}
                        {...this.getCommonRenderProps(EDIT_TYPE.SLIDE)}
                        dataSource={list}
                        ref={refDom => this.setListRef(refDom, this.getListWrapperName(EDIT_TYPE.SLIDE))}
                        getCellView={this.getCellView}
                        getCellHeight={this.getCellHeight}
                        displayStateIds={displayStateIds}
                        viewType={EDIT_TYPE.SLIDE}
                    />
                    { isPressed ? this.renderDraggingSlides() : null }
                </PopupWrapper>
            </RegionWrapper>
        </React.Fragment>);
    };

    /**
     * 把拖拽的slide容器，添加到body上, 单选和多选逻辑都放到这个容器中
     * @returns {*}
     */
    private renderDraggingSlides = () => createPortal(<div style={{
        position: 'absolute',
        left: 0,
        top: 0,
        zIndex: 10001,
    }} ref={(el) => {
        this.setRef('draggingDiv', el);
    }}/>, document.body);

    private setListRef = (refDom, refName: string) => {
        let ref = refDom;
        if (ref) {
            // eslint-disable-next-line react/no-find-dom-node
            ref = findDOMNode(ref);
        }
        this.setRef(refName, ref);
    };

    private renderHistoryAuthorTips() {
        const modifyInfos = this.slideListService.getModifiedSlidesInfos();
        const { historyTipsSlide } = this.getState();
        return (
            <React.Fragment>
                {modifyInfos.map((modifyInfo, index) => {
                    if (modifyInfo) {
                        const { authorName, color, y } = modifyInfo;
                        return this.getHistoryManager()?.renderHistoryAuthorTag({
                            key: index,
                            authorName,
                            color,
                            visible: historyTipsSlide === index && y >= -5,
                            zIndex: 101,
                            y: y - 19,
                        });
                    }
                    return null;
                })}
            </React.Fragment>
        );
    }

    private renderCell = (
        model: ISlideModel,
        listIndex: number,
        viewType: string,
    ) => {
        const {
            currentDragIndex,
            slideWidth,
            slideHeight,
            slideInnerHeight,
            shouldUpdate,
            slideClearanceIndex,
            sectionList,
            masterModelList,
        } = this.getState();
        const {
            handleMouseEnter,
            handleMouseLeave,
            handleMouseDown,
            handleLeftClick,
            getSlideCellClass,
        } = this.slideListService;

        const motionStyle = this.getMotionStyle();
        const isSelected = this.checkIsSelected(model, listIndex);
        const lockInfo = this.getRenderLockInfo(model);
        const dragging = this.slideListService.isModelDragging(model);
        const renderCellIndex = this.slideListService.getRenderCellIndex(listIndex, viewType);
        const clearanceIndex = this.getClearanceIndex(listIndex, sectionList, model, masterModelList);
        const sizeType = this.slideListService.getSizeType();
        return (
            <SlideCellWrapper
                key={model.key}
                extraClass={getSlideCellClass(viewType)}
                motionStyle={motionStyle}
                slideWidth={slideWidth}
                slideHeight={slideHeight}
                slideInnerHeight={slideInnerHeight}
                slideModel={model}
                isSelected={isSelected}
                cellIndex={listIndex}
                handleMouseEnter={handleMouseEnter}
                handleMouseLeave={handleMouseLeave}
                handleMouseDown={handleMouseDown}
                handleLeftClick={handleLeftClick}
                shouldUpdate={shouldUpdate}
                lockInfo={lockInfo}
                draging={dragging}
                currentDragIndex={currentDragIndex}
                clearanceIndex={clearanceIndex}
                ref={(ref) => {
                    this.setRef(this.getCellRefName(model.key), ref);
                }}
                renderClearanceOrDragIndicator={this.renderClearanceOrDragIndicator}
                slideClearanceIndex={slideClearanceIndex}
                renderCellIndex={renderCellIndex}
                sizeType={sizeType}
                getHistoryManager={this.getHistoryManager}
            />
        );
    };

    private getCellRefName(modelKey: string) {
        return `cell-${modelKey}`;
    }

    /**
     * 获取间隙的索引，会分四种情况：版式下的间隙、母版下的间隙、普通页有节时的间隙、普通页没有节时的间隙
     * 母版编辑计算间隙索引的详情可查看：https://doc.weixin.qq.com/doc/w3_Ae4ADwbBAJspJaUHhEDQuW3ew4hCs?scode=AJEAIQdfAAoS5K2cG9Ae4ADwbBAJs
     * @param listIndex 此页面的序号
     * @param sectionList 节列表
     * @param model 当前页的 model
     * @param masterModelList 母版列表
     * @returns 间隙索引
     */
    private getClearanceIndex(
        listIndex: number,
        sectionList: ISectionModel[],
        model: ISlideModel,
        masterModelList: Array<IMasterModel>,
    ): number {
        if (model.isLayout) {
            const masterModel = model.getRelationMasterModel() as IMasterModel;
            const masterIndex = masterModelList.indexOf(masterModel);
            return masterIndex + listIndex + 1;
        }
        if (model.isMaster) {
            const masterIndex = masterModelList.indexOf(model as IMasterModel);
            // 计算此母版之前的所有母版和版式数量之和
            const beforeLength = masterModelList.slice(0, masterIndex).reduce((pre, cur) => {
                const layoutLength = cur.layoutList.length;
                return layoutLength + 1 + pre;
            }, 0);
            return beforeLength;
        }
        if (sectionList?.length) {
            return ClearanceUtils.calcClearanceIndexFromSlideIndex(listIndex, sectionList);
        }
        return listIndex + 1;
    }

    private getMotionStyle() {
        const motionStyle = {
            translateX: 0,
            translateY: 0,
            scale: 1,
            position: 'absolute',
            left: 25,
        };
        return motionStyle;
    }

    private checkIsSelected(model, index) {
        let isSelected = false;
        const {
            originalDragSlide,
            currentSlideIndex,
            currentSlideType,
            sectionList,
            slideClearanceIndex,
        } = this.getState();
        if (this.slideListService.getSelectedArray().includes(model)) {
            isSelected = true;
        } else if (
            currentSlideIndex === index
            && !originalDragSlide && currentSlideType === model.type && slideClearanceIndex === -1) {
            isSelected = true;
        }
        if (sectionList?.length) {
            const selectedSectionIndex = this.slideListService.getSelectedSectionIndex();
            if (sectionList[selectedSectionIndex]?.numSlides === 0) {
                isSelected = false;
            }
        }
        return isSelected;
    }

    private getRenderLockInfo(data) {
        let lockInfo = null;
        if (!this.getState().isShowingHistory) {
            const lockResult = this.getState().lockInfo
                .filter(lock => lock.slide.id === data.id);
            if (lockResult && lockResult.length > 0) {
                // eslint-disable-next-line prefer-destructuring
                lockInfo = lockResult[0];
            }
        }
        return lockInfo;
    }

    // eslint-disable-next-line complexity
    private renderClearanceOrDragIndicator = (options: IRenderClearanceOptions) => {
        const { sectionList, currentDragIndex, editType } = this.getState();
        const { clearanceIndex, style, selectedClearanceIndex, isSectionFolded } = options;
        let sectionIndex: number | undefined = undefined;
        if (sectionList?.length) {
            sectionIndex = ClearanceUtils.calcSectionIndexFromClearanceIndex(clearanceIndex, sectionList);
        }
        if (currentDragIndex === clearanceIndex && !isSectionFolded) {
            return <DragIndicator />;
        }
        return <Clearance
            handleClickClearance={this.slideListService.handleClickClearance}
            clearanceIndex={clearanceIndex}
            sectionIndex={sectionIndex}
            opacity={selectedClearanceIndex === clearanceIndex ? 1 : 0}
            editType={editType}
            style={{
                ...style,
                opacity: selectedClearanceIndex === clearanceIndex ? '1' : '0',
            }}
        />;
    };

    private renderFirstClearance = (clearanceIndex: number): React.ReactElement => {
        const { currentDragIndex, slideClearanceIndex } = this.getState();
        return currentDragIndex === clearanceIndex
            ? <DragIndicator key={Math.random()} />
            : <Clearance
                key={Math.random()}
                handleClickClearance={this.slideListService.handleClickClearance}
                clearanceIndex={clearanceIndex}
                opacity={slideClearanceIndex === 0 ? 1 : 0}
                style={{
                    opacity: slideClearanceIndex === 0 ? '1' : '0',
                }}
            />;
    };

    private renderSectionTitle = (section: ISectionModel, sectionIndex: number) => {
        const { sectionList, isDraggingSection,
            slideModelList, slideClearanceIndex, currentDragIndex } = this.getState();
        let sectionClearanceIndex = ClearanceUtils.calcClearanceIndexFromSectionIndex(sectionIndex, sectionList);
        if (this.sectionFoldedState?.[sectionIndex] || isDraggingSection) {
            sectionClearanceIndex += sectionList[sectionIndex].numSlides;
        }
        let { numSlides } = section;
        if (sectionIndex === sectionList.length - 1) {
            // 兼容节长度与整体页面数量不对应
            numSlides = slideModelList.length - sectionList
                .slice(0, sectionList.length - 1)
                .reduce((acc, cur) => acc + cur.numSlides, 0);
        }
        return (<React.Fragment key={section.key}>
            <SectionTitle
                name={section.name}
                numSlides={numSlides}
                setSectionName={name => this.setSectionName(sectionIndex, name)}
                isFolded={isDraggingSection
                    || (this.sectionFoldedState ? this.sectionFoldedState[sectionIndex] : false)}
                toggleFolded={() => this.toggleSectionFolded(sectionIndex)}
                sectionIndex={sectionIndex}
                isSelected={this.slideListService.isSectionSelected(sectionIndex)}
                setSelected={() => {
                    this.slideListService.setSectionSelected(sectionIndex);
                }}
                historyManager={this.getHistoryManager()}
                nameEditingIndex={this.slideListService.getSectionNameEditingIndex()}
                setIsEditingName={(isEditingName: boolean) => {
                    this.slideListService.setSectionNameEditingIndex(sectionIndex, isEditingName);
                }}
                selectSection={() => this.selectSection(sectionIndex)}
                showContextMenu={(e: MouseEvent) => this.slideListService.showSectionContextMenu(sectionIndex, e)}
                hideContextMenu={() => this.slideListService.hideAllMenu()}
                mousedown={this.slideListService.handleSectionTitleMouseDown}
                canEdit={this.slideListService.draggable()}
                isDraggingSection={isDraggingSection}
                isDragHovering={currentDragIndex === sectionClearanceIndex}
            />
            {this.renderClearanceOrDragIndicator({
                isSectionFolded: !!this.sectionFoldedState?.[sectionIndex],
                clearanceIndex: sectionClearanceIndex,
                selectedClearanceIndex: slideClearanceIndex,
                style: { position: 'absolute' },
            })}
        </React.Fragment>);
    };

    private getCellView = (model: IBaseModel, editType: string): React.ReactElement | null => {
        switch (model.type) {
            case UIModelType.SLIDE:
            case UIModelType.MASTER:
            case UIModelType.LAYOUT:
                return this.renderCell((model as ISlideModel), (model as ISlideModel).listIndex ?? -1, editType);
            case UIModelType.SECTION:
                return this.renderSectionTitle((model as ISectionModel), (model as ISectionModel).sectionIndex);
            case UIModelType.GAP:
                return this.renderFirstClearance((model as IGapModel).gapIndex);
        }
        return null;
    };

    private getCellHeight = (model: IBaseModel): number => {
        const { slideHeight } = this.getState();
        switch (model.type) {
            case UIModelType.SLIDE:
            case UIModelType.MASTER:
            case UIModelType.LAYOUT:
                return slideHeight;
            case UIModelType.SECTION:
                return SectionTitle.SectionTitleHeight;
            default:
                return 0;
        }
    };

    private flattenAndFilterSlideModelIds(dataSource: IBaseModel[][]): string[] {
        const tileArr: IBaseModel[] = [];
        dataSource.forEach(group => group.forEach(item => tileArr.push(item)));
        return tileArr.filter(model => [UIModelType.SLIDE, UIModelType.LAYOUT, UIModelType.MASTER].includes(model.type)).map(model => model.key ?? '');
    }

    private selectSection = (sectionIndex: number) => {
        this.slideListService.selectSection(sectionIndex);
    };

    private setSectionName = (sectionIndex: number, name: string) => {
        this.slideListService.setSectionName(sectionIndex, name);
    };

    private get sectionFoldedState(): boolean[] | null {
        return this.slideListService.getSectionFoldedState();
    }

    private toggleSectionFolded = (sectionIndex: number) => {
        this.slideListService.toggleSectionFolded(sectionIndex);
    };

    /**
     * 保存子组件的ref引用
     * @param name
     * @param ref
     */
    private setRef = (name, ref)  => {
        this.refMap.set(name, ref);
    };

    private getState() {
        const state = this.slideListService.getModel();
        return state as PcLeftThumbnailListModel;
    }

    private getListWrapperName(editType: string): string {
        return this.slideListService.getListWrapperName(editType);
    }

    private getHistoryManager = () => this.slideListService.getHistoryManager();
}
